import RuleOutlined from '@mui/icons-material/RuleOutlined';
import { DailyNavRecord } from 'api/models/DailyNavRecord';
import palette from 'theme/palette';
import { formatProviderCode } from 'utilities';
import { DailyNavReviewStatus } from './daily-nav.api.types';

// "The rules in force here", at both toolbar and row level. Defined once so the two
// cannot drift; deliberately not the default info glyph, which says nothing.
export const DAILY_NAV_RULES_ICON = RuleOutlined;

// Dark surface: never use theme text tokens inside, they are meant for light ones.
// De-emphasis is opacity on white. Shared so the two popouts cannot drift.
export const DAILY_NAV_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      backgroundColor: 'grey.900',
      color: 'common.white',
      maxWidth: 380,
      p: 1.25,
      // Container `color` is not enough: theme/typography.ts bakes text.secondary into
      // the caption and overline VARIANTS, so they carry an explicit dark grey.
      '& .MuiTypography-root': { color: 'inherit' },
    },
  },
  arrow: { sx: { color: 'grey.900' } },
};

export const DAILY_NAV_SPREADSHEET_NAME = 'daily-nav-spreadsheet';
export const DAILY_NAV_SPREADSHEET_TITLE = 'Company';
export const DAILY_NAV_SPREADSHEET_TITLE_ALIAS = 'title';
export const DAILY_NAV_SPREADSHEET_SECTIONS_ALIAS = 'sections';
export const DAILY_NAV_SPREADSHEET_INDENTED_CLASSNAME = 'subtitle-indented';

export const dailyNavCompanyRowAlias = (companyId: number) => String(companyId);

export const dailyNavInvestmentRowAlias = (
  companyId: number,
  fundId: number | null | undefined,
  securityId: number | null | undefined
) => `${companyId}-${fundId ?? 'fund'}-${securityId ?? 'security'}`;

export const DAILY_NAV_COLUMN_TYPES = {
  VALUATION_DATE: 'valuation_date',
  EQUITY_VALUE: 'equity_value',
  PRICE_PER_SHARE: 'price_per_share',
  SHORT_WINDOW_DELTA: 'short_window_delta',
  LONG_WINDOW_DELTA: 'long_window_delta',
  SINCE_LAST_VALUATION_DELTA: 'since_last_valuation_delta',
  SECONDARY_AVERAGE_PRICE: 'secondary_average_price',
  SECONDARY_CHANGE_FROM_MARK: 'secondary_change_from_mark',
  SECONDARY_SHORT_WINDOW_DELTA: 'secondary_short_window_delta',
  SECONDARY_LONG_WINDOW_DELTA: 'secondary_long_window_delta',
  SECONDARY_SINCE_LAST_VALUATION_DELTA: 'secondary_since_last_valuation_delta',
  STATUS: 'status',
} as const;

// Composite column type for a per-provider price column, e.g.
// "secondary_price:CAPLIGHT". Providers are discovered per response, so they
// cannot be static DAILY_NAV_COLUMN_TYPES entries; build and parse the key here
// so the format lives in one place.
const SECONDARY_PRICE_COLUMN_PREFIX = 'secondary_price:';

export const buildSecondaryPriceColumnType = (providerCode: string): string =>
  `${SECONDARY_PRICE_COLUMN_PREFIX}${providerCode}`;

export const parseSecondaryPriceProviderCode = (columnType: string): string | null =>
  columnType.startsWith(SECONDARY_PRICE_COLUMN_PREFIX)
    ? columnType.slice(SECONDARY_PRICE_COLUMN_PREFIX.length)
    : null;

// Provider price columns that render on EVERY Daily NAV table, whether or not a
// company on the page maps them — a firm should see the same Secondary Data
// section from one day to the next rather than watch columns come and go with
// the page's contents. Discovery still adds to this: a provider Alexandria
// starts returning appears with no code change. Display only — nothing filters,
// gates or averages data on this list, so it can never hide a provider.
export const BASELINE_SECONDARY_PROVIDER_CODES = ['CAPLIGHT', 'FORGE', 'ZANBATO'] as const;

// Session counts ride on each record's breakdown; the grid never fetches the
// firm template. Omit them for plain "Short"/"Long" wording.
export const getDailyNavColumnLabels = (
  shortWindowSessions?: number | null,
  longWindowSessions?: number | null
): Record<string, string> => ({
  [DAILY_NAV_COLUMN_TYPES.VALUATION_DATE]: 'Valuation Date',
  [DAILY_NAV_COLUMN_TYPES.EQUITY_VALUE]: 'Equity Value',
  [DAILY_NAV_COLUMN_TYPES.PRICE_PER_SHARE]: 'Price / Share',
  [DAILY_NAV_COLUMN_TYPES.SHORT_WINDOW_DELTA]: shortWindowSessions ? `${shortWindowSessions}-Day Δ` : 'Short Δ',
  [DAILY_NAV_COLUMN_TYPES.LONG_WINDOW_DELTA]: longWindowSessions ? `${longWindowSessions}-Day Δ` : 'Long Δ',
  [DAILY_NAV_COLUMN_TYPES.SINCE_LAST_VALUATION_DELTA]: 'Last Val Δ',
  // Neither of these takes a session count: the average is a price as of today,
  // and the change from mark is measured against our own last valuation rather
  // than over a window.
  [DAILY_NAV_COLUMN_TYPES.SECONDARY_AVERAGE_PRICE]: 'Ave. Price',
  [DAILY_NAV_COLUMN_TYPES.SECONDARY_CHANGE_FROM_MARK]: '±% vs. Mark',
  // Secondary uses the SAME configured windows as the comps -- one
  // resolve_delta_dates for both -- so these must follow the session counts too.
  [DAILY_NAV_COLUMN_TYPES.SECONDARY_SHORT_WINDOW_DELTA]: shortWindowSessions
    ? `${shortWindowSessions}-Day Δ`
    : 'Short Δ',
  [DAILY_NAV_COLUMN_TYPES.SECONDARY_LONG_WINDOW_DELTA]: longWindowSessions
    ? `${longWindowSessions}-Day Δ`
    : 'Long Δ',
  // Same wording as the comp band's equivalent, so the two halves of a row read
  // alike.
  [DAILY_NAV_COLUMN_TYPES.SECONDARY_SINCE_LAST_VALUATION_DELTA]: 'Last Val Δ',
  [DAILY_NAV_COLUMN_TYPES.STATUS]: 'Status',
});

// Per-provider price columns have no static label — the provider list is
// discovered per response — so their header is derived from the column type's
// parsed provider code. Everything else comes from the caller's label map,
// which is session-count aware and therefore cannot be a module constant.
export const getDailyNavColumnLabel = (
  columnType: string,
  columnLabels: Record<string, string>
): string => {
  const providerCode = parseSecondaryPriceProviderCode(columnType);
  if (providerCode) {
    return `${formatProviderCode(providerCode)} Price`;
  }
  return columnLabels[columnType] ?? columnType;
};

// Column-group bands rendered above the column labels, mirroring the monitoring
// prototype's PREVIOUS VALUATION / NAV CONSIDERATIONS sections.
export interface DailyNavSection {
  label: string;
  columnTypes: string[];
  // Completed at render time with the metric's display name from the breakdown
  // payload, so the header follows whatever metric produced the deltas.
  labelFromMetric?: boolean;
}

// The Secondary Data section's member list depends on which provider price
// columns are present in a given render, so the section list as a whole is
// built from the render's columnTypeOrder rather than kept as a static const.
export const buildDailyNavSections = (columnTypeOrder: string[]): DailyNavSection[] => {
  const secondaryProviderColumnTypes = columnTypeOrder.filter(
    columnType => parseSecondaryPriceProviderCode(columnType) !== null
  );

  return [
    {
      label: 'Previous Valuation',
      columnTypes: [
        DAILY_NAV_COLUMN_TYPES.VALUATION_DATE,
        DAILY_NAV_COLUMN_TYPES.EQUITY_VALUE,
        DAILY_NAV_COLUMN_TYPES.PRICE_PER_SHARE,
      ],
    },
    // Per-provider secondary-market prices (dynamic — one column per provider
    // discovered in this render), their mean and its distance from our mark,
    // then their averaged short-window/long-window deltas.
    {
      label: 'Secondary Data',
      columnTypes: [
        ...secondaryProviderColumnTypes,
        DAILY_NAV_COLUMN_TYPES.SECONDARY_AVERAGE_PRICE,
        DAILY_NAV_COLUMN_TYPES.SECONDARY_CHANGE_FROM_MARK,
        DAILY_NAV_COLUMN_TYPES.SECONDARY_SHORT_WINDOW_DELTA,
        DAILY_NAV_COLUMN_TYPES.SECONDARY_LONG_WINDOW_DELTA,
        DAILY_NAV_COLUMN_TYPES.SECONDARY_SINCE_LAST_VALUATION_DELTA,
      ],
    },
    // Averaged metric change across the company config's comps, from Alexandria:
    //  short-window/long-window, and since the last valuation.
    {
      label: 'Public Comp',
      labelFromMetric: true,
      columnTypes: [
        DAILY_NAV_COLUMN_TYPES.SHORT_WINDOW_DELTA,
        DAILY_NAV_COLUMN_TYPES.LONG_WINDOW_DELTA,
        DAILY_NAV_COLUMN_TYPES.SINCE_LAST_VALUATION_DELTA,
      ],
    },
    {
      label: 'NAV Considerations',
      columnTypes: [DAILY_NAV_COLUMN_TYPES.STATUS],
    },
  ];
};

// Mirrors DailyNavRecord.Status' labels on the backend, which the alert email
// also renders -- one vocabulary, so a row reads the same on screen and in the
// inbox. These are STATES, not button verbs; the dialog keeps the action wording
// in REVIEW_PRICE_LABEL.
export const DAILY_NAV_STATUS_LABELS: Record<DailyNavReviewStatus, string> = {
  reviewed: 'Reviewed',
  in_progress: 'In Progress',
  review_price: 'Needs Review',
  review_status: 'No Action Needed',
};

export const isDailyNavReviewComplete = (input: {
  reviewed_by_scalar: boolean;
  discussed_with_fund: boolean;
  price_adjusted: boolean | null;
  noteCount: number;
}): boolean =>
  input.reviewed_by_scalar &&
  input.discussed_with_fund &&
  input.price_adjusted !== null &&
  input.noteCount >= 1;

export const missingDailyNavReviewFields = (input: {
  reviewed_by_scalar: boolean;
  discussed_with_fund: boolean;
  price_adjusted: boolean | null;
  noteCount: number;
}): string[] => {
  const missing: string[] = [];
  if (!input.reviewed_by_scalar) {
    missing.push('Reviewed by Scalar');
  }
  if (!input.discussed_with_fund) {
    missing.push('Discussed with Fund');
  }
  if (input.price_adjusted === null) {
    missing.push('Price adjusted');
  }
  if (input.noteCount < 1) {
    missing.push('a review note');
  }
  return missing;
};

export type DailyNavReviewState = {
  status: DailyNavReviewStatus;
  label: string;
  color: string;
  textColor: string;
  hoverColor: string;
};

// theme/types.ts declares these through MUI's PaletteOptions, where every field
// is optional, and omits `success` altogether -- so narrow once here rather than
// at each use. palette.ts sets all three.
const semantic = palette as unknown as Record<
  'error' | 'warning' | 'success',
  { main: string; dark: string; contrastText: string }
>;

// Presentation only. The state is decided server-side by
// review.derive_review_status and arrives as `status`; nothing here re-derives it.
//
// Colours are the app's semantic tokens -- error / warning / success -- so this
// grid stays in step with the rest of the app rather than inventing a palette.
// The email mirrors the resolved hexes in alerts._REVIEW_STATUS_COLORS,
// and its labels come from the same DailyNavRecord.Status.
const REVIEW_STATES: Record<DailyNavReviewStatus, DailyNavReviewState> = {
  review_price: {
    status: DailyNavRecord.status.REVIEW_PRICE,
    label: DAILY_NAV_STATUS_LABELS.review_price,
    color: semantic.error.main,
    textColor: semantic.error.contrastText,
    hoverColor: semantic.error.dark,
  },
  in_progress: {
    status: DailyNavRecord.status.IN_PROGRESS,
    label: DAILY_NAV_STATUS_LABELS.in_progress,
    color: semantic.warning.main,
    textColor: semantic.warning.contrastText,
    hoverColor: semantic.warning.dark,
  },
  reviewed: {
    status: DailyNavRecord.status.REVIEWED,
    label: DAILY_NAV_STATUS_LABELS.reviewed,
    color: semantic.success.main,
    textColor: semantic.success.contrastText,
    hoverColor: semantic.success.dark,
  },
  review_status: {
    status: DailyNavRecord.status.REVIEW_STATUS,
    label: DAILY_NAV_STATUS_LABELS.review_status,
    color: semantic.success.main,
    textColor: semantic.success.contrastText,
    hoverColor: semantic.success.dark,
  },
};

// An unresolved status reads as nothing to do, not an alarm nobody can
// explain.
const FALLBACK_REVIEW_STATE = REVIEW_STATES[DailyNavRecord.status.REVIEW_STATUS];

export const getDailyNavReviewState = (record: DailyNavRecord): DailyNavReviewState =>
  REVIEW_STATES[record.status as DailyNavReviewStatus] ?? FALLBACK_REVIEW_STATE;

// The dialog's title -- the action, not the row's state.
export const REVIEW_PRICE_LABEL = 'Review Price';

export type DailyNavReportTab = 'validate' | 'preview' | 'finalize' | 'send';

export const DAILY_NAV_REPORT_PHASES: { tab: DailyNavReportTab; label: string }[] = [
  { tab: 'validate', label: 'Validate Report' },
  { tab: 'preview', label: 'Preview Report' },
  { tab: 'finalize', label: 'Finalize Report' },
  { tab: 'send', label: 'Send Report' },
];

// Report-date query param. Deliberately distinct from the shared 'date' param, which
// Intelligence's measurement-date filter owns; NAV dates are free-standing daily values.
export const DAILY_NAV_REPORT_DATE_PARAM = 'nav_date';

// Staff must write a real attestation, not the old type-the-word gate.
const STAFF_FINALIZE_MAGIC_WORDS = new Set(['finalize', 'finalized']);

export const isValidStaffFinalizeMessage = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length > 0 && !STAFF_FINALIZE_MAGIC_WORDS.has(trimmed.toLowerCase());
};
